import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Zap,
  Home,
  Leaf,
  FileText,
  BarChart3,
  ShieldCheck,
  ArrowUpRight,
  ArrowRight,
  MapPin,
  Search as SearchIcon,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/flagged/photo-1566838616631-f2618f74a6a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxob21lJTIwZW5lcmd5JTIwZWZmaWNpZW5jeXxlbnwwfHx8fDE3ODk4MTkxNDJ8MA&ixlib=rb-4.1.0&q=85";

const RATINGS = [
  { grade: "A", range: "92+", color: "bg-epc-a", note: "Most efficient" },
  { grade: "B", range: "81–91", color: "bg-epc-b" },
  { grade: "C", range: "69–80", color: "bg-epc-c", note: "Target for lettings" },
  { grade: "D", range: "55–68", color: "bg-epc-d" },
  { grade: "E", range: "39–54", color: "bg-epc-e", note: "MEES minimum" },
  { grade: "F", range: "21–38", color: "bg-epc-f" },
  { grade: "G", range: "1–20", color: "bg-epc-g", note: "Least efficient" },
];

const FAQ = [
  {
    q: "What exactly is an EPC?",
    a: "An Energy Performance Certificate rates a property's energy efficiency from A (most efficient) to G (least). It's required whenever a building is sold, let or newly built in the UK.",
  },
  {
    q: "How long does an EPC last?",
    a: "EPCs are valid for 10 years. After that, a new assessment is needed if the property is marketed for sale or let.",
  },
  {
    q: "How do I find an EPC assessor?",
    a: "You can book an assessment via theepcman.co.uk (providing EPCs since 2011) or search accredited assessors on The EPC Register at theepcregister.co.uk.",
  },
  {
    q: "What is MEES?",
    a: "Minimum Energy Efficiency Standards. Rented domestic properties must currently reach EPC band E; proposed changes aim for band C for new tenancies.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [postcode, setPostcode] = useState("");
  const [house, setHouse] = useState("");

  function handleCheck(e) {
    e.preventDefault();
    const q = postcode.trim();
    if (!q) return;
    const h = house.trim();
    navigate(`/check?postcode=${encodeURIComponent(q)}${h ? `&house=${encodeURIComponent(h)}` : ""}`);
  }

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <Logo className="h-10 w-10" />
            <span className="font-heading text-lg tracking-tight">chatepc<span className="text-epc-a">.co.uk</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#ratings" className="hover:text-foreground">Ratings</a>
            <a href="#how" className="hover:text-foreground">How it helps</a>
            <a href="#faq" className="hover:text-foreground">FAQ</a>
          </nav>
          <Link to="/chat" data-testid="nav-open-chat">
            <Button className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white font-medium">
              Open chat <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground border border-border px-3 py-1">
              <span className="h-1.5 w-1.5 bg-epc-a rounded-full" /> UK Energy Performance Certificates
            </div>
            <h1 className="mt-6 font-heading font-extrabold text-5xl md:text-7xl leading-[0.95] tracking-tight">
              Ask anything about your
              <span className="inline-block px-3 mx-2 bg-epc-a text-white">EPC</span>
              — get answers in seconds.
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-2xl">
              ChatEPC is a specialist assistant for UK homeowners, landlords and letting agents. Understand your rating, improve your score, and stay compliant with MEES — powered by Claude Sonnet 5.
            </p>

            <form
              onSubmit={handleCheck}
              className="mt-8 flex items-stretch max-w-xl border border-foreground/20 bg-white"
              data-testid="hero-check-form"
            >
              <Input
                data-testid="hero-house"
                value={house}
                onChange={(e) => setHouse(e.target.value)}
                placeholder="House no. (optional)"
                className="w-28 sm:w-44 border-0 border-r border-border rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <div className="pl-3 flex items-center text-muted-foreground">
                <MapPin className="h-4 w-4" />
              </div>
              <Input
                data-testid="hero-postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="UK postcode e.g. LS1 4DL"
                className="flex-1 min-w-0 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Button
                type="submit"
                data-testid="hero-check-btn"
                disabled={!postcode.trim()}
                className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white h-11 px-5"
              >
                <SearchIcon className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Check EPC</span>
              </Button>
            </form>
            <p className="mt-2 text-xs text-muted-foreground">Live data from the UK government register (England &amp; Wales).</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/chat" data-testid="hero-start-chat">
                <Button size="lg" className="rounded-none bg-foreground text-background hover:bg-foreground/90 font-medium h-12 px-6">
                  Start chatting free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#ratings">
                <Button size="lg" variant="outline" className="rounded-none h-12 px-6 border-foreground/20">
                  See rating scale
                </Button>
              </a>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">No sign-up. Chats saved to this browser.</p>
          </div>

          <div className="md:col-span-5 relative grain-overlay">
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-border">
              <img src={HERO_IMG} alt="UK home with solar panels" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-black/30" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">Current rating</div>
                <div className="mt-1 flex items-end gap-3">
                  <div className="h-16 w-16 bg-epc-c grid place-items-center text-black font-heading font-extrabold text-4xl">C</div>
                  <div className="text-sm opacity-90 leading-tight">
                    Improve to <span className="font-heading font-bold">B</span> with cavity insulation & smart heating controls.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rating scale */}
      <section id="ratings" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
            <div className="md:col-span-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">The EPC scale</div>
              <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight">A means efficient. G means expensive to run.</h2>
            </div>
            <div className="md:col-span-6 text-muted-foreground">
              Every UK property has a rating from A to G based on a SAP score out of 100+. The higher the letter, the cheaper the bills and the lower the emissions.
            </div>
          </div>
          <div className="border border-border">
            {RATINGS.map((r, i) => (
              <div
                key={r.grade}
                className={`flex items-center gap-6 border-b border-border last:border-b-0 ${r.color}`}
                data-testid={`rating-row-${r.grade}`}
              >
                <div className="w-20 md:w-28 h-16 md:h-20 grid place-items-center font-heading font-extrabold text-3xl md:text-5xl text-black">
                  {r.grade}
                </div>
                <div className="flex-1 flex flex-wrap items-baseline gap-3 pr-6 text-black/85">
                  <div className="font-heading text-xl md:text-2xl">SAP {r.range}</div>
                  {r.note && <div className="text-sm md:text-base opacity-75">{r.note}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature bento */}
      <section id="how" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Why use ChatEPC</div>
          <h2 className="mt-3 font-heading text-4xl md:text-5xl tracking-tight max-w-3xl">
            One assistant, every question about your certificate.
          </h2>

          <div className="mt-12 grid md:grid-cols-6 gap-4">
            <Card icon={<FileText className="h-5 w-5" />} title="Read your EPC" span="md:col-span-3" accent="bg-epc-a">
              Understand every number on your certificate — SAP score, potential rating, CO₂ estimates and recommended measures.
            </Card>
            <Card icon={<BarChart3 className="h-5 w-5" />} title="Improve your rating" span="md:col-span-3" accent="bg-epc-c">
              Get prioritised, cost-aware upgrades — insulation, glazing, heating controls, heat pumps and solar PV.
            </Card>
            <Card icon={<ShieldCheck className="h-5 w-5" />} title="Stay compliant" span="md:col-span-2" accent="bg-epc-d">
              MEES rules for lettings, exemptions and upcoming EPC C requirements — explained in plain English.
            </Card>
            <Card icon={<Home className="h-5 w-5" />} title="Buying or selling?" span="md:col-span-2" accent="bg-epc-e">
              What an EPC means for mortgage lenders, buyers and green mortgage discounts.
            </Card>
            <Card icon={<Leaf className="h-5 w-5" />} title="Cut bills & carbon" span="md:col-span-2" accent="bg-epc-b">
              Compare measures by payback, grant availability (ECO4, BUS) and lifetime CO₂ savings.
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">FAQ</div>
            <h2 className="mt-3 font-heading text-4xl tracking-tight">Straight answers.</h2>
            <p className="mt-4 text-muted-foreground">Still curious? The chat can go much deeper — including your specific property.</p>
            <Link to="/chat" data-testid="faq-open-chat" className="inline-flex items-center gap-2 mt-6 text-epc-a font-medium">
              Ask ChatEPC <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="border-t border-border">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left font-heading text-lg py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-widest text-white/60">Ready when you are</div>
            <h2 className="mt-3 font-heading text-4xl md:text-6xl tracking-tight leading-[1]">
              Your EPC, decoded.
              <br />
              <span className="text-epc-b">Start the conversation.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/chat" data-testid="cta-open-chat">
              <Button size="lg" className="rounded-none bg-white hover:bg-white/90 text-black font-medium h-14 px-8">
                Open chat <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Logo className="h-7 w-7" />
            <span>chatepc.co.uk · specialist AI for UK EPCs</span>
          </div>
          <div>© {new Date().getFullYear()} ChatEPC. Informational only, not legal advice.</div>
        </div>
      </footer>
    </div>
  );
}

function Card({ icon, title, children, span = "", accent = "bg-epc-a" }) {
  return (
    <div className={`group relative border border-border p-6 bg-white hover:bg-neutral-50 transition-colors ${span}`}>
      <div className={`inline-flex items-center gap-2 ${accent} text-black px-2 py-1 text-xs font-medium`}>
        {icon}
        <span>{title}</span>
      </div>
      <p className="mt-5 text-foreground/80 leading-relaxed">{children}</p>
    </div>
  );
}
